import type enStrings from "./en.json";

/**
 * `@pontimed/provider-ui` dictionaries (the profile sheet, cards, reviews),
 * one per mandatory locale, all with en.json's keys — the loader types below
 * fail the typecheck when a file drifts. They load next to the Lingui catalog
 * (`loadLocale`, src/i18n/i18n.ts), so the active one is ready before React
 * renders; en is always loaded as the per-key fallback. The 7 secondary
 * locales read en.
 */
export type ProviderStrings = typeof enStrings;

const LOADERS: Record<string, () => Promise<{ default: ProviderStrings }>> = {
  en: () => import("./en.json"),
  ro: () => import("./ro.json"),
  bg: () => import("./bg.json"),
  de: () => import("./de.json"),
  fr: () => import("./fr.json"),
  hu: () => import("./hu.json"),
  it: () => import("./it.json"),
  el: () => import("./el.json"),
};

const loaded = new Map<string, ProviderStrings>();

async function loadOne(locale: string): Promise<void> {
  const load = LOADERS[locale];
  if (!load || loaded.has(locale)) return;
  loaded.set(locale, (await load()).default);
}

export async function loadProviderStrings(locale: string): Promise<void> {
  await Promise.all([loadOne("en"), loadOne(locale)]);
}

/** The dictionaries to read, in order: the locale's own, then en. */
export function providerStringsChain(locale: string): ProviderStrings[] {
  return [loaded.get(locale), loaded.get("en")].filter((d): d is ProviderStrings => !!d);
}
